// ============================================
// PAGE - Mi Caja (turno en vivo)
// ============================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from '@almacenes/ui';
import {
  useMiTurno,
  useCerrarTurno,
  useMovimientosDeTurno,
  useRegistrarMovimiento,
  useAnularMovimiento,
  useUIStore,
  useSession,
  type RegistrarMovimientoDto,
  type TurnoCaja,
} from '@almacenes/core';
import { MdArrowBack, MdAdd, MdLock, MdDelete, MdRefresh } from 'react-icons/md';
import { RegistrarMovimientoModal } from './components/RegistrarMovimientoModal';
import { CerrarTurnoModal } from './components/CerrarTurnoModal';
import { PaginacionTabla } from './components/PaginacionTabla';
import { TableSkeletonRows } from './components/TableSkeletonRows';
import { DesglosePorMedioPago } from './components/DesglosePorMedioPago';
import { KpiTile } from './components/KpiTile';
import { ImprimirComprobanteTurnoModal } from './components/ImprimirComprobanteTurnoModal';
import { FormularioApertura } from './components/FormularioApertura';
import { formatCurrency, formatearFechaHora } from './utils/formato';
import { badgeEstadoMovimiento } from './utils/badges';

export const MiCajaPage = () => {
  const navigate = useNavigate();
  const addToast = useUIStore((state) => state.addToast);
  const { data: miTurno, isLoading: isLoadingTurno, isFetching: isFetchingTurno, refetch: refetchTurno } = useMiTurno({ refetchOnFocus: true });
  const turno = miTurno?.turno ?? null;
  const turnosOtrasSucursales = miTurno?.turnosOtrasSucursales ?? [];
  const { data: session } = useSession();
  const [page, setPage] = useState(0);
  const { data: movimientos, isLoading: isLoadingMovimientos, isFetching: isFetchingMovimientos, refetch: refetchMovimientos } = useMovimientosDeTurno(turno?.id, page);
  // Refetch en segundo plano (no la carga inicial, que ya tiene su propio spinner): atenúa
  // los datos mientras se actualizan para que el cambio no se sienta como un salto brusco.
  const actualizandoTurno = isFetchingTurno && !isLoadingTurno;
  const actualizandoMovimientos = isFetchingMovimientos && !isLoadingMovimientos;
  const cerrarTurno = useCerrarTurno();
  const registrarMovimiento = useRegistrarMovimiento(turno?.id);
  const anularMovimiento = useAnularMovimiento(turno?.id);

  const [showRegistrarModal, setShowRegistrarModal] = useState(false);
  const [showCerrarModal, setShowCerrarModal] = useState(false);
  const [turnoCerrado, setTurnoCerrado] = useState<TurnoCaja | null>(null);

  const handleActualizar = () => {
    refetchTurno();
    if (turno) refetchMovimientos();
  };

  const handleRegistrarMovimiento = (dto: RegistrarMovimientoDto) => {
    registrarMovimiento.mutate(dto, {
      onSuccess: () => {
        setShowRegistrarModal(false);
        addToast('success', 'Movimiento registrado');
      },
      onError: (err: any) => addToast('error', err?.message || 'Error al registrar el movimiento'),
    });
  };

  const handleAnularMovimiento = (movimientoId: number) => {
    anularMovimiento.mutate(movimientoId, {
      onSuccess: () => addToast('success', 'Movimiento anulado'),
      onError: (err: any) => addToast('error', err?.message || 'Error al anular el movimiento'),
    });
  };

  const handleCerrarTurno = (efectivoContado: number | undefined) => {
    if (!turno) return;
    cerrarTurno.mutate(
      { turnoId: turno.id, dto: { efectivoContado } },
      {
        onSuccess: (turnoResultante) => {
          setShowCerrarModal(false);
          setTurnoCerrado(turnoResultante);
          addToast('success', 'Caja cerrada exitosamente');
        },
        onError: (err: any) => {
          if (err?.statusCode === 404) {
            setShowCerrarModal(false);
            addToast('error', 'Los datos de tu turno estaban desactualizados. Se actualizaron, revisa e intenta de nuevo.');
            return;
          }
          addToast('error', err?.message || 'Error al cerrar la caja');
        },
      }
    );
  };

  return (
    <div className="px-4 md:px-6 lg:px-8 pb-8">
      <div className="mb-6 flex items-center gap-4">
        <button onClick={() => navigate('/caja-360')} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <MdArrowBack className="text-2xl" style={{ color: '#43BFFF' }} />
        </button>
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#11194F' }}>Mi caja</h1>
          <p className="text-gray-600 mt-1 flex items-center gap-2">
            Tu turno de caja
            {actualizandoTurno && <Spinner size="sm" />}
          </p>
          {/* En negocios con varias sucursales bajo el mismo RUT el nombre se repite
              idéntico en todas — mostrar la dirección evita cerrar/abrir caja
              pensando que se está en una sucursal distinta a la real. */}
          {session?.almacen?.direccion && (
            <p className="text-xs text-gray-400 mt-0.5">📍 {session.almacen.direccion}</p>
          )}
        </div>
        <button
          onClick={handleActualizar}
          disabled={isFetchingTurno || isFetchingMovimientos}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ color: '#11194F' }}
        >
          <MdRefresh className={`text-lg ${isFetchingTurno || isFetchingMovimientos ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {isLoadingTurno ? (
        <div className="flex justify-center py-12"><Spinner size="lg" /></div>
      ) : !turno ? (
        <div className="space-y-4">
          {turnosOtrasSucursales.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-amber-800 mb-1">
                {turnosOtrasSucursales.length === 1
                  ? 'Tenés un turno abierto en otra sucursal:'
                  : 'Tenés turnos abiertos en otras sucursales:'}
              </p>
              <ul className="text-sm text-amber-700 space-y-0.5">
                {turnosOtrasSucursales.map((t) => (
                  <li key={t.turnoId}>
                    📍 {t.direccionAlmacen ?? `Sucursal #${t.almacenId}`} · abierto el {formatearFechaHora(t.fechaHoraApertura)}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-amber-600 mt-2">
                Ingresá a esa sucursal para cerrarlo{turnosOtrasSucursales.length > 1 ? 's' : ''}.
              </p>
            </div>
          )}
          <FormularioApertura />
        </div>
      ) : (
        <div className="space-y-6">
          <p className="text-sm text-gray-500">
            Abierto el {formatearFechaHora(turno.fechaHoraApertura)} · Monto inicial {formatCurrency(turno.montoInicial)}
          </p>

          <div className={`grid grid-cols-2 md:grid-cols-5 gap-4 transition-opacity duration-300 ${actualizandoTurno ? 'opacity-60' : ''}`}>
            <KpiTile label="Total ventas" value={formatCurrency(turno.totalVentas)} />
            <KpiTile label="Ingresos (todos los medios)" value={formatCurrency(turno.totalIngresos)} />
            {turno.totalIngresosEfectivo != null && (
              <KpiTile label="Ingresos en efectivo" value={formatCurrency(turno.totalIngresosEfectivo)} />
            )}
            <KpiTile label="Egresos" value={formatCurrency(turno.totalEgresos)} />
            <KpiTile label="Efectivo esperado" value={formatCurrency(turno.efectivoEsperado)} highlight />
          </div>

          <div className={`transition-opacity duration-300 ${actualizandoTurno ? 'opacity-60' : ''}`}>
            <DesglosePorMedioPago turno={turno} />
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="secondary" onClick={() => setShowRegistrarModal(true)}>
              <MdAdd className="inline-block mr-2 text-xl" />
              Registrar movimiento
            </Button>
            <Button variant="danger" onClick={() => setShowCerrarModal(true)}>
              <MdLock className="inline-block mr-2 text-xl" />
              Cerrar caja
            </Button>
          </div>

          <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden transition-opacity duration-300 ${actualizandoMovimientos ? 'opacity-60' : ''}`}>
            <h2 className="text-lg font-bold px-6 py-4 border-b border-gray-200" style={{ color: '#11194F' }}>
              Movimientos del turno
            </h2>
            {!isLoadingMovimientos && (!movimientos || movimientos.content.length === 0) ? (
              <p className="text-center py-8 text-gray-500">Todavía no hay movimientos registrados</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F3F4F9]">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-bold text-[#11194F]">Tipo</th>
                        <th className="px-6 py-3 text-left text-sm font-bold text-[#11194F]">Glosa</th>
                        <th className="px-6 py-3 text-right text-sm font-bold text-[#11194F]">Monto</th>
                        <th className="px-6 py-3 text-center text-sm font-bold text-[#11194F]">Estado</th>
                        <th className="px-6 py-3 text-left text-sm font-bold text-[#11194F]">Fecha</th>
                        <th className="px-6 py-3 text-center text-sm font-bold text-[#11194F]">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoadingMovimientos ? (
                        <TableSkeletonRows columns={6} />
                      ) : (
                        movimientos!.content.map((m, index) => (
                          <tr key={m.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-6 py-3 text-sm">{m.tipo}</td>
                            <td className="px-6 py-3 text-sm text-gray-600">{m.glosa}</td>
                            <td className="px-6 py-3 text-sm text-right">{formatCurrency(m.monto)}</td>
                            <td className="px-6 py-3 text-center">
                              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${badgeEstadoMovimiento(m.estado)}`}>
                                {m.estado}
                              </span>
                            </td>
                            <td className="px-6 py-3 text-sm text-gray-600">{formatearFechaHora(m.fechaHoraCreacion)}</td>
                            <td className="px-6 py-3 text-center">
                              {m.estado !== 'RECHAZADO' && (
                                <button
                                  onClick={() => handleAnularMovimiento(m.id)}
                                  disabled={anularMovimiento.isPending}
                                  className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                                  title="Anular"
                                >
                                  <MdDelete className="text-xl" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {movimientos && (
                  <PaginacionTabla
                    page={page}
                    totalPages={movimientos.totalPages}
                    totalElements={movimientos.totalElements}
                    cantidadEnPagina={movimientos.content.length}
                    etiqueta="movimientos"
                    onChange={setPage}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {showRegistrarModal && (
        <RegistrarMovimientoModal
          onClose={() => setShowRegistrarModal(false)}
          onConfirmar={handleRegistrarMovimiento}
          isLoading={registrarMovimiento.isPending}
        />
      )}

      {showCerrarModal && turno && (
        <CerrarTurnoModal
          turno={turno}
          onClose={() => setShowCerrarModal(false)}
          onConfirmar={handleCerrarTurno}
          isLoading={cerrarTurno.isPending}
        />
      )}

      {turnoCerrado && (
        <ImprimirComprobanteTurnoModal turno={turnoCerrado} onClose={() => setTurnoCerrado(null)} />
      )}
    </div>
  );
};
